import { useCallback } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { useChannelDispatch } from '@/features/channel-router'

/**
 * Copy shown when a creator opens the code surface from a chat next-action.
 * The code surface renders for any persona (ActiveSurface keys only on
 * activeRailItem), but the 'code' rail icon stays hidden for creators — so
 * point them at the explicit persona switch ('All tools') for persistent
 * access. No emoji per feedback_no_emoji_icons.md; named so the two openCode
 * call sites cannot drift.
 */
export const OPEN_CODE_CREATOR_NOTICE =
  'Viewing the generated code. Switch to All tools in the top bar to keep code tools open.'
import type { UiConformancePanelItem } from '@/features/validation/ui-ontology-panel-model'
import type { MutationFailedInfo } from './types'

/**
 * Minimal validation-issue ask. `ValidationIssueInfoMessage` (the chat-bubble
 * shape) is structurally assignable, so chat call sites pass `msg` unchanged;
 * panel surfaces (Validator list/alert, UI-ontology conformance items)
 * construct this directly from their row models.
 *
 * `blockId: ''` marks a project-scope issue with no single owning block
 * (e.g. manifest-level conformance items) — dispatched without an element
 * subtree, mirroring the mutation-failed branch.
 */
export interface ValidationIssueAsk {
  kind: 'validation-issue'
  blockId: string
  elementId: string
  elementType: string
  ruleId: string
  message: string
}

/**
 * Payload accepted by useAskCogoHandler (v2.2 Phase 029).
 *
 * Discriminator: `kind` field.
 * - 'validation-issue' -> element-subtree refinement.
 * - 'mutation-failed'  -> mutation-tool refinement (new in Phase 029).
 */
export type AskCogoPayload = ValidationIssueAsk | MutationFailedInfo

/** Shown when an Ask-cogo click references a block that left the project. */
export const STALE_BLOCK_FEEDBACK =
  'That block no longer exists — re-run validation to get current results.'

/**
 * Maps a UI-ontology conformance item to an Ask-cogo payload. Items carry
 * their owning block in `bindings.block` when known; hosts with a narrower
 * scope (e.g. the block inspector) pass it as `fallbackBlockId`. Items with
 * neither resolve to the project-scope dispatch path (`blockId: ''`).
 */
export function conformanceItemToAsk(
  item: UiConformancePanelItem,
  fallbackBlockId?: string | null,
): ValidationIssueAsk {
  return {
    kind: 'validation-issue',
    blockId: item.bindings?.block ?? fallbackBlockId ?? '',
    elementId: item.bindings?.focus ?? item.schemaField ?? '',
    elementType: '',
    ruleId: item.code,
    message: item.message,
  }
}

/**
 * Returns a callback that dispatches a refinement request for either a
 * validation-issue (existing path, byte-identical to v2.1) or a
 * mutation-failed message (Phase 029 — CONTEXT.md D-05).
 *
 * The intent string is the WHOLE request. Until 2026-08-19 each branch also
 * built a `RefinementContext` naming the block, the element and (via
 * `extractElementSubtree`) its subtree, and passed it to `dispatch` — where the
 * channel router discarded it unread. The rule id, block and element the router
 * can actually act on are the ones spelled into the intent below.
 *
 * Intent formats:
 *   validation-issue: "Fix: {message} (rule: {ruleId}, element: {blockId}/{elementId})"
 *   block-scope:      "Fix: {message} (rule: {ruleId}, block: {blockId})" — no element binding
 *   project-scope:    "Fix: {message} (rule: {ruleId})"
 *   mutation-failed:  "Fix: {summary} (tool: {tool})"
 */
export function useAskCogoHandler() {
  const { dispatch } = useChannelDispatch()
  const blocks = useStudioStore((s) => s.currentProject?.blocks)

  return useCallback(
    (msg: AskCogoPayload) => {
      if (msg.kind === 'validation-issue') {
        if (!msg.blockId) {
          // Project-scope issue (no owning block).
          dispatch(`Fix: ${msg.message} (rule: ${msg.ruleId})`)
          return
        }
        const block = blocks?.[msg.blockId]
        if (!block) {
          // The referenced block left the project (e.g. a later generation
          // replaced all blocks) — say so instead of silently doing nothing.
          toast.error(STALE_BLOCK_FEEDBACK)
          return
        }
        // 'unknown' is the legacy no-focus-binding fallback persisted chat
        // bubbles may still carry — treat it like an absent element id so
        // the dispatched intent never asserts a fictitious element.
        const elementId = msg.elementId === 'unknown' ? '' : msg.elementId
        const intent = elementId
          ? `Fix: ${msg.message} (rule: ${msg.ruleId}, element: ${msg.blockId}/${elementId})`
          : `Fix: ${msg.message} (rule: ${msg.ruleId}, block: ${msg.blockId})`
        dispatch(intent)
        return
      }

      // ---- mutation-failed branch (Phase 029 D-05) ----
      dispatch(`Fix: ${msg.summary} (tool: ${msg.tool})`)
    },
    [dispatch, blocks],
  )
}

/**
 * Returns a callback that switches the IDE shell to the Evidence rail.
 * Used by the chat-rail's overflow link
 * ("+ N more violation(s) · open Proof").
 */
export function useOpenValidatorTabHandler() {
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)

  return useCallback(() => {
    setActiveRailItem('evidence')
  }, [setActiveRailItem])
}

/**
 * v2.2 Phase 029 — sibling of useOpenValidatorTabHandler. Used by
 * mutation-applied bubble's "View project ontology" link to deep-link
 * the project ontology inspector to a specific IRI.
 *
 * Effects: setActiveRailItem('library') + setOntologySelectedIri(iri).
 *
 * `iri === null` is supported and clears the selection.
 */
export function useOpenOntologyPanelHandler() {
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)
  const setOntologySelectedIri = useStudioStore((s) => s.setOntologySelectedIri)

  return useCallback(
    (iri: string | null) => {
      setActiveRailItem('library')
      setOntologySelectedIri(iri)
    },
    [setActiveRailItem, setOntologySelectedIri],
  )
}

/**
 * F31 — shared "Open Code" handler for the two byte-identical openCode bodies
 * (chat-messages MessageRow success bubble + chat-page-progress completed
 * bubble). Navigates to the code surface WITHOUT mutating persona.
 *
 * Root cause this replaces: both call sites used to call
 * `setStudioPersona('developer')` as a side-effect of a view action, silently
 * and permanently promoting an Essentials creator to the developer persona
 * (persisted to localStorage, unlocking every advanced rail across reloads).
 * The escalation was never needed for the view — ActiveSurface renders
 * CodeSurface purely on `activeRailItem === 'code'` — so we drop it.
 *
 * - developer → pure navigation (no spurious re-set, no toast).
 * - creator   → navigate to code WITHOUT changing persona + one informational
 *   toast pointing at the persona switcher. The 'code' rail icon stays hidden
 *   for creators (it is developer-only), so this is a one-time view; the toast
 *   tells them where to make it permanent.
 */
export function useOpenCodeHandler() {
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)

  return useCallback(() => {
    const isCreator = useStudioStore.getState().studioPersona !== 'developer'
    setActiveRailItem('code')
    if (isCreator) {
      toast.info(OPEN_CODE_CREATOR_NOTICE)
    }
  }, [setActiveRailItem])
}
